import { toAnalyticsEvent } from './kwikformEvents';

/**
 * GoKwik KwikForms SDK integration.
 * Configured via VITE_KWIKFORMS_* in .env (see .env.example).
 *
 * The origin is both the script host and the postMessage origin the form
 * replies from, so the two can never drift apart between sandbox and
 * production. listenForFormEvents below is what checks the second of those.
 */
const rawOrigin = (import.meta.env.VITE_KWIKFORMS_ORIGIN || 'https://sandbox.pdp.gokwik.co').trim();

export const KWIKFORMS_ORIGIN = rawOrigin.replace(/\/$/, '');

const MERCHANT_ID = (import.meta.env.VITE_KWIKFORMS_MID || '').trim();
const ENVIRONMENT = (import.meta.env.VITE_KWIKFORMS_ENVIRONMENT || 'sandbox').trim();

const SCRIPT_ELEMENT_ID = 'kwikpass-sdk';
const INSTANCE_TIMEOUT_MS = 10000;
const INSTANCE_POLL_MS = 100;

// The SDK attaches its instance to window some time after the script's own load
// event, so onload alone is not a usable readiness signal.
const waitForSdkInstance = () =>
    new Promise((resolve, reject) => {
        const startedAt = Date.now();

        const poll = () => {
            if (window.__KP_LOGIN_SDK_INSTANCE__) {
                resolve(window.__KP_LOGIN_SDK_INSTANCE__);
                return;
            }
            if (Date.now() - startedAt >= INSTANCE_TIMEOUT_MS) {
                reject(new Error('KwikForms SDK loaded but never exposed __KP_LOGIN_SDK_INSTANCE__'));
                return;
            }
            setTimeout(poll, INSTANCE_POLL_MS);
        };

        poll();
    });

// Cached for the lifetime of the page: the SDK is a global singleton, so a
// remount (route change, StrictMode) must reuse it rather than inject a second
// script tag.
let sdkPromise = null;

export const loadKwikFormsSdk = () => {
    if (sdkPromise) return sdkPromise;

    if (!MERCHANT_ID) {
        sdkPromise = Promise.reject(
            new Error('KwikForms merchant ID is missing — set VITE_KWIKFORMS_MID'),
        );
        return sdkPromise;
    }

    // Must be set before the script evaluates — the SDK reads it on startup.
    window.merchantInfo = {
        ...window.merchantInfo,
        environment: ENVIRONMENT,
        mid: MERCHANT_ID,
        integrationType: 'CUSTOM_HEADLESS',
        type: 'merchantInfo',
    };

    sdkPromise = new Promise((resolve, reject) => {
        if (document.getElementById(SCRIPT_ELEMENT_ID)) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.id = SCRIPT_ELEMENT_ID;
        script.src = `${KWIKFORMS_ORIGIN}/kwikpass/plugin/build/kp-custom-merchant.js`;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('KwikForms SDK script failed to load'));

        document.head.appendChild(script);
    }).then(waitForSdkInstance);

    return sdkPromise;
};

/**
 * Hidden custom fields, merged into the single customFieldValues object the
 * SDK accepts. Hidden fields are never shown to the customer and their keys are
 * ours to choose — GoKwik passes them through to the submission webhook as-is.
 *
 * Only the full landing-page URL is sent. The lead gateway already derives the
 * slug and every UTM/click-id parameter from a referral URL via
 * parseReferralUrlParams, so passing the URL reuses that logic instead of
 * duplicating it here — and keeps us to one field, well under the SDK's
 * 15-field-per-form limit.
 */
export const buildHiddenCustomFields = () => ({
    curelo_referral_url: { value: window.location.href, hidden: true },
});

/**
 * Passes the form's own lifecycle messages (start, error, submitted) to onEvent
 * as analytics events, and returns a function that stops listening.
 *
 * Messages from any origin other than GoKwik's are ignored: any script on the
 * page can call postMessage, and only GoKwik's iframe is trusted to say that a
 * form was submitted. The events carry no personal data — see kwikformEvents.js.
 */
export const listenForFormEvents = (onEvent) => {
    const handleMessage = (event) => {
        if (event.origin !== KWIKFORMS_ORIGIN) return;

        const analyticsEvent = toAnalyticsEvent(event.data);
        if (analyticsEvent) onEvent(analyticsEvent);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
};

// Google Tag Manager is loaded in index.html and reads window.dataLayer. Keys
// with no value are dropped rather than pushed as undefined, because GTM would
// treat that as overwriting whatever an earlier push had set.
export const pushToDataLayer = (entry) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
        Object.fromEntries(Object.entries(entry).filter(([, value]) => value !== undefined)),
    );
};
